// BHP-1050: read the current user's account from Backend2.0.
import { ApiError } from '../../../shared/http/ApiError';
import { httpClient } from '../../../shared/http/httpClient';
import {
  normalizeCollection,
  normalizeItem,
  type RawHydraCollection
} from '../../../shared/api/hydra';
import env from '../../../config/env';
import type { AccountData } from '../domain/accountData';
import type UserData from '../../../Models/UserData';

/**
 * App-editable subset of the account (merge-patch). Only the name fields are
 * app-owned; email and password are managed in Authentik (BHP-1093), so they are
 * deliberately not patchable from here.
 */
export interface AccountPatch {
  firstName?: string;
  insertion?: string | null;
  lastName?: string;
}

interface OidcProfile {
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  groups?: unknown;
}

function mapGroupsToRoles(groups: unknown): string[] {
  const roles = ['ROLE_USER'];
  if (Array.isArray(groups) && groups.includes('bizhost-admins')) {
    roles.push('ROLE_ADMIN');
  }
  return roles;
}

/**
 * The id-token claims react-oidc-context stored under
 * `oidc.user:<authority>:<clientId>`.
 *
 * One reader for both callers below. They used to each do their own
 * localStorage read and JSON parse, and had already drifted apart on an empty
 * `sub`: one accepted it, the other did not.
 */
function readOidcProfile(): OidcProfile | null {
  const stored = localStorage.getItem(
    `oidc.user:${env.auth.authority}:${env.auth.clientId}`
  );
  if (stored === null) {
    return null;
  }
  try {
    return (JSON.parse(stored) as { profile?: OidcProfile }).profile ?? null;
  } catch {
    return null;
  }
}

/** The Authentik subject of the signed-in user, or null when there is none. */
function currentSubject(): string | null {
  const sub = readOidcProfile()?.sub;

  return typeof sub === 'string' && sub !== '' ? sub : null;
}

/**
 * Identity bootstrap from the OIDC id-token claims. This is the reliable source
 * for "who am I" — unlike GET /api/accounts, which for an admin returns every
 * account.
 */
export function getCurrentUserFromToken(): UserData | null {
  const profile = readOidcProfile();
  if (profile === undefined || profile === null || profile.sub === undefined) {
    return null;
  }
  return {
    uuid: profile.sub,
    username: profile.preferred_username ?? profile.name ?? profile.email ?? '',
    firstName: profile.given_name ?? '',
    insertion: null,
    lastName: profile.family_name ?? '',
    email: profile.email ?? '',
    roles: mapGroupsToRoles(profile.groups),
    apiToken: '',
    customers: null,
    active: true,
    deletedOn: null,
    createdOn: new Date(),
    updatedOn: null
  };
}

/**
 * The account of the signed-in user, asked for by Authentik subject.
 *
 * BHP-1194: this used to read `/accounts` and take `items[0]`, on the
 * assumption that customer scoping left the caller with only their own row. It
 * does not. Two colleagues at the same customer are in each other's scope, so
 * the portal ran on whichever account came back first — and then offered that
 * account's customers in the switcher. An admin is unrestricted and gets every
 * account, which made the same bug certain rather than merely likely.
 *
 * The backend now narrows the collection to the caller's own row (admins
 * excepted, who must keep seeing everything), and this asks by subject on top.
 * Either alone would fix the normal caller; both are needed to fix the admin
 * too.
 */
export async function fetchCurrentAccount(): Promise<AccountData | null> {
  const subject = currentSubject();

  // Not knowing who the caller is, is a broken bootstrap — not "this account
  // has no customer yet". OnboardingGate tells those two apart (BHP-1073) and
  // offers a retry for the first; returning null here would send someone who
  // has customers into the sign-up flow instead.
  if (subject === null) {
    throw new ApiError({
      title: 'Not signed in',
      detail: '',
      status: 401
    });
  }

  const raw = await httpClient.get<RawHydraCollection<Record<string, unknown>>>(
    '/accounts',
    { params: { authentikSub: subject } }
  );
  const { items } = normalizeCollection<AccountData>(raw);

  // API Platform ignores a query parameter it does not know, silently. If this
  // filter is ever renamed or dropped — or the frontend ships ahead of the
  // backend — the response widens again and taking the first row would restore
  // the exact bug this fixes, with nothing to show for it. Saying so out loud
  // costs one comparison.
  if (items.length > 1) {
    throw new ApiError({
      title: 'Could not identify your account',
      detail: '',
      status: 500
    });
  }

  const account = items[0];
  if (account === undefined) {
    // A genuine "no account for this subject": a sign-up whose provisioning
    // has not run yet. That is the onboarding path, and it is a successful
    // empty answer rather than a failure.
    return null;
  }
  // BHP-1053: harden against `customerUuids` arriving as a JSON object instead
  // of an array (the backend indexes the customers relation by uuid, so a
  // non-empty list serializes as `{"uuid":"uuid"}`). Normalize to a plain list
  // so every consumer (onboarding gate, active-customer bootstrap) can rely on
  // array semantics. Backend fix: array_values() in AccountMapper.
  return { ...account, customerUuids: toStringList(account.customerUuids) };
}

/**
 * Update the current account via PATCH (merge-patch) on Backend2.0. Only the
 * provided fields change; validation errors surface as an ApiError with
 * field-level violations (problem+json).
 */
export async function updateAccount(
  uuid: string,
  patch: AccountPatch
): Promise<AccountData> {
  const raw = await httpClient.patch<Record<string, unknown>>(
    `/accounts/${uuid}`,
    patch
  );
  return normalizeItem<AccountData>(raw);
}

/** Coerce an array- or object-shaped value into a list of strings. */
function toStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String);
  }
  if (value !== null && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).map(String);
  }
  return [];
}

/**
 * Map the Backend2.0 account onto the app's legacy `UserData` shape so existing
 * consumers keep working during the migration. Fields absent from the backend
 * output are defaulted; full customer objects are resolved later (BHP-1053), so
 * `customers` stays null here.
 */
export function toUserData(account: AccountData): UserData {
  return {
    uuid: account.uuid,
    username: account.displayName ?? account.email ?? '',
    firstName: account.firstName ?? '',
    insertion: account.insertion,
    lastName: account.lastName ?? '',
    email: account.email ?? '',
    roles: account.roles,
    apiToken: '',
    customers: null,
    active: account.active === 1,
    deletedOn: account.deletedOn !== null ? new Date(account.deletedOn) : null,
    createdOn: new Date(account.createdOn),
    updatedOn: account.updatedOn !== null ? new Date(account.updatedOn) : null
  };
}
